import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { Bot, ArrowRight } from "lucide-react";
import PublicChatWidget from "@/components/PublicChatWidget";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public standalone agent demo page. Meant to be shared directly with
 * prospects during sales calls. No CRM chrome, no branded site —
 * just: hero + AI chat widget powered by the requested agent.
 */
export default function AgentPublicDemo() {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/agents/${id}/meta`);
        setAgent(data);
      } catch {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-neutral-500">Loading…</div>;
  }
  if (notFound || !agent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-neutral-400 flex-col gap-4">
        <p>This demo link is no longer active.</p>
        <Link to="/" className="underline text-amber-400">Go home</Link>
      </div>
    );
  }

  return (
    <div className="co-public co-dark min-h-screen" data-testid="agent-public-demo-page">
      <Toaster position="top-right" richColors />

      <header className="co-nav co-nav-dark">
        <div className="co-nav-inner">
          <Link to="/" className="co-brand">
            <span className="co-brand-dot" />
            <span className="co-brand-text">CLIENTS<span style={{ color: "#FFB020" }}>ONRAMP</span></span>
          </Link>
          <nav className="co-nav-links">
            <Link to="/pricing-public" className="co-nav-link co-nav-link-dark">Pricing</Link>
            <Link to="/#contact" className="co-nav-cta">Book demo →</Link>
          </nav>
        </div>
      </header>

      <section className="co-section co-section-hero-ind" style={{ paddingBottom: 60 }}>
        <div className="co-container" style={{ maxWidth: 780 }}>
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 border border-amber-500/40 bg-amber-500/10">
            <Bot className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs font-mono uppercase tracking-widest text-amber-400">Live agent preview</span>
          </div>
          <h1 className="co-h1-ind" style={{ fontSize: "clamp(36px, 6vw, 60px)" }}>
            Meet <span style={{ color: "#FFB020" }}>{agent.name}</span>.
          </h1>
          <p className="co-lead-dark" style={{ maxWidth: 620, marginTop: 24 }}>
            {agent.tagline || "This is the exact AI agent that will answer for your business. Chat with it right now — ask anything you'd expect a customer to ask."}
          </p>
          <div style={{ marginTop: 40 }} className="flex flex-col sm:flex-row items-start gap-4">
            <div className="text-xs font-mono uppercase tracking-widest text-white/50">
              Tap the amber button
            </div>
            <ArrowRight className="w-4 h-4 text-amber-400 hidden sm:block" />
            <div className="text-xs font-mono uppercase tracking-widest text-white/50">
              Bottom-right of your screen
            </div>
          </div>
        </div>
      </section>

      <section className="co-section co-section-how" style={{ paddingTop: 0 }}>
        <div className="co-container" style={{ maxWidth: 780 }}>
          <div className="co-eyebrow-dark">What to try</div>
          <ul className="co-timeline-full-list" style={{ marginTop: 20 }}>
            <li><span className="text-white/85">Ask about pricing, availability, or a specific service you offer.</span></li>
            <li><span className="text-white/85">Try to book an appointment — the agent will guide you through it.</span></li>
            <li><span className="text-white/85">Ask something weird or off-topic. See how it handles it gracefully.</span></li>
          </ul>
          <div style={{ marginTop: 48 }}>
            <Link to="/#contact" className="co-btn-yellow">
              I want one for my business <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="co-footer">
        <div className="co-container co-footer-inner">
          <div>
            <div className="co-brand">
              <span className="co-brand-dot" />
              <span className="co-brand-text">CLIENTSONRAMP</span>
            </div>
            <p className="co-footer-tag">The AI back office for local service businesses.</p>
          </div>
          <div className="co-footer-copy">© {new Date().getFullYear()} Clients OnRamp</div>
        </div>
      </footer>

      <PublicChatWidget agent={agent} />
    </div>
  );
}
